import { hasFeature } from '@pubstack/common/src/rolesAndFeatures'
import { Button } from '@pubstack/ui/components/Button'
import { Skeleton } from '@pubstack/ui/components/Skeleton'
import { useAtomValue } from 'jotai'
import { FunctionComponent, PropsWithChildren, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAbTest } from '~/api/app/ab-test.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { CodeSnippet } from '~/components/CodeSnippet'
import { ContentCard } from '~/components/ContentCard'
import { currentScopeState } from '~/state'
import { SETTINGS_NAV_CONFIG } from '~/utils/settings'

const InstructionCard: FunctionComponent<PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <ContentCard color={SETTINGS_NAV_CONFIG.abTest.color} className="flex flex-col gap-6">
    <h2 className="m-0 border-b border-solid border-platinum pb-5 font-rubik text-xl leading-[30px] font-normal text-jet">{title}</h2>
    <div className="flex flex-col gap-4">{children}</div>
  </ContentCard>
)

const Instruction: FunctionComponent<PropsWithChildren> = ({ children }) => <div className="text-base leading-[26px] text-jet">{children}</div>
const SnippetLabel: FunctionComponent<PropsWithChildren> = ({ children }) => <div className="text-sm leading-[22px] text-slate-grey">{children}</div>

// CodeSnippet pads its inner wrappers itself: zero them and size the code block instead
const snippetClassName = (size: 'long' | 'short') => (size === 'long' ? '[&_code]:w-[700px] [&_code]:p-3 [&_div]:p-0' : '[&_code]:w-[260px] [&_code]:p-3 [&_div]:p-0')

export const ViewAbTestPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const { data: abTest, isLoading, isError } = useAbTest(id)
  const currentScope = useAtomValue(currentScopeState)
  const user = useAuthenticatedUser()
  const hasAdx = hasFeature(user.currentScopeId, user.features, 'adx')

  // useQuery already toasts the error
  useEffect(() => {
    if (isError) navigate('/settings/ab-test')
  }, [isError, navigate])

  if (isLoading || isError) {
    return <Skeleton bigger width="200px" />
  }

  const variants = abTest?.variants ?? []

  return (
    <div className="mx-auto my-5 flex max-w-[1000px] flex-col gap-5">
      {currentScope.pbjs && (
        <InstructionCard title="A/B test in Prebid">
          <Instruction>To monitor A/B tests from Prebid, add the following meta to the targeted pages header:</Instruction>
          {variants.map((variant, index) => (
            <div key={`Prebid${index}`} className={snippetClassName('long')}>
              <CodeSnippet contentText={`<meta name="pbstck_context:pbstck_ab_test" content="${variant.variant}">`} trackingLabel="common-settings-ab-test-html-tag-copy" />
            </div>
          ))}
        </InstructionCard>
      )}
      {hasAdx && (
        <InstructionCard title="A/B test in GAM">
          <Instruction>To monitor A/B tests from GAM, set the following key-value as custom dim</Instruction>
          <div>
            <SnippetLabel>Key name</SnippetLabel>
            <div className={snippetClassName('short')}>
              <CodeSnippet contentText="pbstck_ab_test" trackingLabel="common-settings-ab-test-key-name-copy" />
            </div>
          </div>
          {variants.map((variant, index) => (
            <div key={`GAM${index}`}>
              <SnippetLabel>Value</SnippetLabel>
              <div className={snippetClassName('short')}>
                <CodeSnippet contentText={variant.variant} trackingLabel="common-settings-ab-test-value-copy" />
              </div>
            </div>
          ))}
          <Instruction>Use the setTargeting function:</Instruction>
          <div>
            <div className={snippetClassName('long')}>
              <CodeSnippet contentText="googletag.pubads().setTargeting('pbstck_ab_test','<value>')" trackingLabel="common-settings-ab-test-targeting-function-copy" />
            </div>
            <div className="ml-3 pt-px text-sm leading-[22px] text-slate-grey">Example : googletag.pubads().setTargeting('pbstck_ab_test','variant_a')</div>
          </div>
        </InstructionCard>
      )}
      <div className="text-right">
        <Button onClick={() => navigate('/settings/ab-test')} trackingLabel="common-settings-ab-test-view-close">
          Close
        </Button>
      </div>
    </div>
  )
}
